import React, { useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';
import dayjs from 'dayjs';
import { getPublicationsDetailStack } from 'features/publications/detail/actions';
import { setTopNavColorDark } from 'features/navigation/header/actions';
import styled, { css } from 'styled-components';
import { palette, darkTheme } from 'theme';
import { MuiThemeProvider } from '@material-ui/core/styles';
import loadable from '@loadable/component';
import PrerenderSafeLazyLoad from 'components/PrerenderSafeLazyLoad';

import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Collapse from '@material-ui/core/Collapse';
import Divider from '@material-ui/core/Divider';

import Container from 'components/Container';
import ContentTypography from 'components/ContentTypography';

import { SectionHeader, SectionWrapper, SectionGrid } from 'components/Section';
import ArrowLeftIcon from 'mdi-material-ui/ArrowLeft';

import MetaTags from 'components/MetaTags';
import SwiftypeTags from 'components/SwiftypeTags';

import formatPublicationDetails from '../utilities/formatPublicationDetails';

const FeaturedCard = loadable(() => import('../elements/FeaturedCard'));
const RelatedPublications = loadable(() =>
  import('components/RelatedPublications')
);

const Figure = loadable(() => import('components/Figure'));
const SocialShare = loadable(() => import('components/SocialShare'));
const SimpleFooterCTA = loadable(() => import('components/SimpleFooterCTA'));
const CallsToAction = loadable(() => import('components/CallsToAction'));

const MixedRelatedContent = loadable(
  () => import('components/RelatedContent'),
  {
    resolveComponent: components => components.MixedRelatedContent
  }
);
const RelatedContentWrapper = loadable(
  () => import('components/RelatedContent'),
  {
    resolveComponent: components => components.RelatedContentWrapper
  }
);

const ProgressWrapper = styled.div`
  text-align: center;
  padding: 40px;
`;

const PublicationContainer = styled(Container)`
  margin-top: 2.5vh;
  margin-bottom: 2.5vh;
`;

const FeaturedTypography = styled(Typography)`
  margin-top: 16px;
  margin-right: 70px;
`;

const AuthorTypography = styled(Typography)`
  font-size: 16px;
  font-weight: normal;
  color: ${palette.text.secondary};
`;

const AffiliationsList = styled.ul`
  border-left: 4px solid ${palette.grey[100]};
  list-style-type: none;
`;

const JournalInfo = styled.div`
  margin-bottom: 36px;
`;

const JournalTypography = styled(Typography)`
  display: inline-block;
  margin-right: 36px;
  a {
    color: #000;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Abstract = styled.div`
  margin: 36px 0;
`;

const AbstractButton = styled(Button)`
  margin-left: -8px;
  margin-top: -30px;
`;

const AbstractCollapse = styled(Collapse)`
  position: relative;
  margin-bottom: 10px;
  &:after {
    transition: all 1s linear;
    background: linear-gradient(
      rgba(255, 255, 255, 0) 30%,
      rgba(255, 255, 255, ${props => (props.in ? 0 : 1)})
    );
    position: absolute;
    z-index: 1;
    display: block;
    width: 100%;
    height: 80px;
    content: '';
    bottom: 0;
  }
`;

const PublicationSocialShare = styled(SocialShare)`
  margin-bottom: 36px;
  margin-left: -12px;
`;

const MetaItemTypography = styled(Typography)`
  display: flex;
  align-items: center;
  font-size: 12px;
  margin: 12px 0;
  img {
    height: 24px;
    margin-right: 8px;
    width: auto;
  }
  ${props =>
    props.href &&
    css`
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    `}
`;

const MetaGridItems = styled(Grid)`
  border: 4px solid ${palette.grey[100]};
  border-width: 4px 0 0 0;
  padding-top: 36px;
  @media (min-width: 600px) {
    padding-top: 0;
    border-width: 0 0 0 4px;
  }
`;

const MetaDivider = styled(Divider)`
  margin: 0 24px;
`;

const MetaGridItem = styled(({ label, items, ...other }) =>
  items && items.length > 0 ? (
    <Grid item xs={6} sm={12} {...other}>
      <Typography variant="overline" gutterBottom>
        {label}
      </Typography>
      {items.map((item, idx) => (
        <MetaItemTypography
          variant="subtitle2"
          key={idx}
          component={item.page_url ? 'a' : 'h6'}
          href={item.page_url}
        >
          {item.icon && <img src={item.icon.url} alt={item.title} />}
          {item.title}
        </MetaItemTypography>
      ))}
    </Grid>
  ) : null
)`
  padding: 0 24px 36px 24px;
`;

const PublicationsDetail = ({
  stack,
  loading,
  setTopNavColorDark,
  getPublicationsDetailStack,
  match
}) => {
  useEffect(() => {
    setTopNavColorDark();
  }, [setTopNavColorDark]);

  useEffect(() => {
    getPublicationsDetailStack(match.url);
  }, [getPublicationsDetailStack, match.url]);

  useEffect(() => {
    setAbstractOpen(!stack.is_featured);
  }, [stack]);

  const [affilationsOpen, setAffiliationsOpen] = useState(false);
  const [abstractOpen, setAbstractOpen] = useState(false);

  const pubDetails = formatPublicationDetails(stack);

  return (
    <Fragment>
      {!loading ? (
        <>
          <MetaTags stack={stack} />
          <SwiftypeTags
            stack={stack}
            title={stack.title}
            contentType="Publication"
            featuredPublication={stack.is_featured}
            publishedAt={pubDetails.publish_date}
            publishedAtMonth={dayjs
              .utc(pubDetails.publish_date)
              .startOf('month')
              .format()}
            journal={pubDetails.journal}
            journalLink={pubDetails.journal_link?.href}
            pubmedId={pubDetails.pmid}
            author={pubDetails.authors}
          />
          <PublicationContainer>
            <Button href="/publications">
              <ArrowLeftIcon /> All Publications
            </Button>
          </PublicationContainer>
          <PublicationContainer>
            <Grid container spacing={40}>
              <Grid
                item
                xs={12}
                sm={7}
                md={9}
                data-swiftype-name="body"
                data-swiftype-type="text"
              >
                <JournalInfo>
                  <JournalTypography variant="overline">
                    {pubDetails.journal},{' '}
                    {dayjs(pubDetails.publish_date).format('MMM YYYY')}
                  </JournalTypography>
                  {pubDetails.pmid && (
                    <JournalTypography variant="overline">
                      <a
                        href={`https://pubmed.ncbi.nlm.nih.gov/${pubDetails.pmid}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        PubMed: {pubDetails.pmid}
                      </a>
                    </JournalTypography>
                  )}
                  <JournalTypography variant="overline">
                    <a
                      href={pubDetails.journal_link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {pubDetails.journal_link.title}
                    </a>
                  </JournalTypography>
                </JournalInfo>
                <Typography variant="h2" component="h1" gutterBottom>
                  {stack.title}
                </Typography>
                {pubDetails.authors.map((author, idx) => (
                  <AuthorTypography
                    variant="subtitle1"
                    inline
                    gutterBottom
                    key={idx}
                  >
                    {author}
                    {idx + 1 !== pubDetails.authors.length ? ', ' : ' '}
                  </AuthorTypography>
                ))}
                {pubDetails.affiliations && pubDetails.affiliations.length > 0 && (
                  <>
                    <Button
                      onClick={() => setAffiliationsOpen(!affilationsOpen)}
                      size="small"
                    >
                      {affilationsOpen ? '-' : '+'} Affiliations
                    </Button>
                    <Collapse in={affilationsOpen}>
                      <AffiliationsList>
                        {pubDetails.affiliations.map((affiliation, idx) => (
                          <li key={idx}>
                            <AuthorTypography
                              variant="subtitle1"
                              gutterBottom
                              key={idx}
                            >
                              {affiliation}
                            </AuthorTypography>
                          </li>
                        ))}
                      </AffiliationsList>
                    </Collapse>
                  </>
                )}
                <Abstract>
                  <AbstractCollapse in={abstractOpen} collapsedHeight={'175px'}>
                    <Typography variant="h5" component="h3" gutterBottom>
                      Abstract
                    </Typography>
                    <ContentTypography
                      variant="body2"
                      dangerouslySetInnerHTML={{
                        __html: pubDetails.abstract
                      }}
                    />
                  </AbstractCollapse>
                  {!abstractOpen && (
                    <AbstractButton
                      onClick={() => setAbstractOpen(!abstractOpen)}
                      size="small"
                      color="primary"
                    >
                      Read More
                    </AbstractButton>
                  )}
                </Abstract>
                {stack.is_featured && (
                  <FeaturedCard
                    elevation={0}
                    featured
                    background={palette.grey[50]}
                  >
                    <FeaturedTypography
                      variant="h3"
                      component="h2"
                      gutterBottom
                    >
                      {stack.featured_title}
                    </FeaturedTypography>
                    {stack.featured_content &&
                      stack.featured_content.map((block, idx) => (
                        <Fragment key={idx}>
                          {block.rich_text && (
                            <ContentTypography
                              variant="body2"
                              dangerouslySetInnerHTML={{
                                __html: block.rich_text.text
                              }}
                            />
                          )}
                          {block.figure &&
                            block.figure.figure_reference.length > 0 && (
                              <Figure {...block.figure.figure_reference[0]} />
                            )}
                        </Fragment>
                      ))}
                  </FeaturedCard>
                )}
                <PrerenderSafeLazyLoad>
                  <PublicationSocialShare
                    url={window.location.href}
                    color="default"
                    direction="horizontal"
                    justify="flex-start"
                  />
                </PrerenderSafeLazyLoad>
              </Grid>
              <Grid item xs={12} sm={5} md={3}>
                <PrerenderSafeLazyLoad>
                  <MetaGridItems container>
                    <MetaGridItem
                      label="Product Used"
                      items={_.get(stack.categories_tags, 'product')}
                    />
                    <MetaGridItem
                      label="Application"
                      items={_.get(stack.categories_tags, 'application')}
                    />
                    <MetaGridItem
                      label="Research Area"
                      items={_.get(stack.categories_tags, 'research_area')}
                    />
                    <MetaGridItem
                      label="Editing Method"
                      items={_.get(stack.categories_tags, 'editing_method')}
                    />
                    <MetaGridItem
                      label="Model Organism"
                      items={_.get(stack.categories_tags, 'model_organism')}
                    />
                    <MetaGridItem
                      label="Paper Type"
                      items={_.get(stack.categories_tags, 'paper_type')}
                    />
                    {stack.sidebar_ctas && stack.sidebar_ctas.length > 0 && (
                      <>
                        <MetaDivider />
                        <CallsToAction
                          actionItems={stack.sidebar_ctas}
                          stacked
                        />
                      </>
                    )}
                  </MetaGridItems>
                </PrerenderSafeLazyLoad>
              </Grid>
            </Grid>
          </PublicationContainer>
          <PrerenderSafeLazyLoad>
            <SectionWrapper backgroundcolor={palette.grey[50]}>
              <SectionGrid container size="small" justify="center">
                <SectionHeader variant="overline">
                  Related Publications
                </SectionHeader>

                <RelatedPublications
                  padded
                  researchArea={_.get(stack.categories_tags, 'research_area')}
                />
              </SectionGrid>
            </SectionWrapper>
          </PrerenderSafeLazyLoad>

          {stack.footer_cta && stack.footer_cta.length > 0 && (
            <PrerenderSafeLazyLoad>
              <SimpleFooterCTA {...stack.footer_cta[0]} />
            </PrerenderSafeLazyLoad>
          )}
          {stack.related_content && (
            <PrerenderSafeLazyLoad>
              <RelatedContentWrapper backgroundcolor={palette.grey[800]}>
                <MuiThemeProvider theme={darkTheme}>
                  <SectionHeader variant="overline" noGutter>
                    Explore Resources
                  </SectionHeader>
                  <Grid item xs={12}>
                    <MixedRelatedContent contentItems={stack.related_content} />
                  </Grid>
                </MuiThemeProvider>
              </RelatedContentWrapper>
            </PrerenderSafeLazyLoad>
          )}
        </>
      ) : (
        <ProgressWrapper>
          <CircularProgress />
        </ProgressWrapper>
      )}
    </Fragment>
  );
};

PublicationsDetail.propTypes = {
  stack: PropTypes.object,
  loading: PropTypes.bool
};

export default connect(
  state => ({
    stack: state.publicationsDetail.stack,
    loading: state.publicationsDetail.loading
  }),
  {
    getPublicationsDetailStack,
    setTopNavColorDark
  }
)(PublicationsDetail);
